import { clsx } from 'clsx'
import type { ChangeEvent, InputHTMLAttributes, MouseEvent, ReactElement, Ref } from 'react'

import { Tooltip } from '#design-system/component/Tooltip/Tooltip'

/** `type` and `role` are what make the input a switch; `checked` / `onChange` are replaced by the
 * controlled pair below; `onClick` is managed through onChange; `disabled` managed by `disabledMessage`. */
type NativeSwitchProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'checked' | 'children' | 'defaultChecked' | 'disabled' | 'onChange' | 'onClick' | 'role' | 'type'
>

export interface SwitchProps extends NativeSwitchProps {
  isChecked: boolean
  onChange: (isChecked: boolean) => void
  /** Presence implies disabled; the message surfaces via a wrapping Tooltip. */
  disabledMessage?: string | undefined
  ref?: Ref<HTMLInputElement>
}

/**
 * Binary on/off toggle. State changes should impact immediately, as opposed to checkbox/radio, which effect on submit.
 * On labels: There's no canonical label position for a switch (not a form element), so the control renders alone
 * — pair it with your own `<label>`, or pass `aria-label` when no visible label.
 */
export const Switch = (props: SwitchProps): ReactElement => {
  const { isChecked, onChange, disabledMessage, className, ref, ...rest } = props
  const isDisabled = disabledMessage !== undefined

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    if (isDisabled) {
      return
    }
    onChange(event.target.checked)
  }

  // aria-disabled keeps the input hoverable and focusable for the Tooltip, so block interactions directly.
  const handleClick = (event: MouseEvent<HTMLInputElement>): void => {
    if (isDisabled) {
      event.preventDefault()
    }
  }

  const control = (
    <input
      {...rest}
      ref={ref}
      aria-disabled={isDisabled || undefined}
      checked={isChecked}
      className={clsx('doss-switch', className)}
      onChange={handleChange}
      onClick={handleClick}
      role="switch"
      type="checkbox"
    />
  )

  return isDisabled ? <Tooltip title={disabledMessage}>{control}</Tooltip> : control
}
